'use client';

import type { FC, ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { useScreenLayout } from './useScreenLayout';
import { useUiMotionEnabled } from '../hooks/useMotion';

interface ScreenFixedBottomProps {
  children: ReactNode;
  className?: string;
  /** Override the auto-gate. Left undefined the scrim follows whether there are
   * children; set it explicitly only when content is present but must NOT draw a
   * scrim (e.g. an `invisible` wrapper during a spin), or vice versa. */
  showBlur?: boolean;
}

export const ScreenFixedBottom: FC<ScreenFixedBottomProps> = ({ children, className, showBlur }) => {
  const { setFixedBottom, clearFixedBottom } = useScreenLayout();
  const motionEnabled = useUiMotionEnabled();

  // Read through a ref in the teardown effect: the preference must not be a
  // dependency, or flipping it would unmount/remount the bar for real.
  const motionRef = useRef(motionEnabled);
  motionRef.current = motionEnabled;

  // Auto-gate: no children → no scrim. So a bar that renders nothing this frame
  // (e.g. its button is hidden mid-roll) can't leave an empty blur behind. Pass a
  // `null`/`false` child when there's nothing to show rather than an empty wrapper.
  const blur = showBlur ?? (children != null && children !== false);

  // update content on every change — NO cleanup here, so a content swap doesn't
  // momentarily clear the bottom (which collapsed its height var and jumped scroll)
  useLayoutEffect(() => {
    setFixedBottom(children, className, blur, motionRef.current);
  }, [children, className, blur, setFixedBottom]);

  // clear only on real unmount
  useLayoutEffect(() => () => clearFixedBottom(motionRef.current), [clearFixedBottom]);

  return null;
};
